import type {
	CharErrorDetail,
	CharDisplayMode,
	CharHostContext,
	CharRequestDisplayModeDetail,
	CharSizeChangedDetail,
	ConnectOptions,
} from './types'
import {
	DEFAULT_AVAILABLE_DISPLAY_MODES,
	isDisplayMode,
	resolvePolicyDisplayMode,
} from './display-mode-policy'
import { mergeHostContext } from './utils/host-context-merge'
import { parseBooleanAttribute } from './utils/parse-boolean-attribute'
import { registerChar, type CharAgentElement } from './web-component'

const SHELL_OPEN_CHANGE_EVENT = 'char-shell-open-change'
const SHELL_Z_INDEX = 2147483000
const FULLSCREEN_BREAKPOINT = 1024
const PANEL_WIDTH = 420
const PIP_WIDTH = 400
const PIP_HEIGHT = 60
const PIP_POSITION: CharPipPosition = 'bottom-center'
const SHELL_HEALTH_TIMEOUT_MS = 10_000
const SAFE_BOTTOM = 'calc(16px + env(safe-area-inset-bottom, 0px))'

const THEME_LIGHT = {
	background: 'oklch(1 0 0)',
	foreground: 'oklch(0.141 0.005 285.823)',
	border: 'oklch(0.92 0.004 286.32)',
	mutedForeground: 'oklch(0.552 0.016 285.938)',
	primary: 'oklch(0.21 0.006 285.885)',
} as const
const THEME_DARK = {
	background: 'oklch(0.141 0.005 285.823)',
	foreground: 'oklch(0.985 0 0)',
	border: 'oklch(1 0 0 / 10%)',
	mutedForeground: 'oklch(0.705 0.015 286.067)',
	primary: 'oklch(0.92 0.004 286.32)',
} as const
const BODY_MARGIN_TRANSITION = 'margin-right 220ms ease'
const SHELL_LAYOUT_TRANSITION =
	'opacity 220ms ease, width 220ms ease, min-width 220ms ease, flex-basis 220ms ease'
const SHELL_REVEAL_FADE_MS = 280
const PIP_MIN_COLLAPSED_WIDTH = 70
const PIP_MIN_COLLAPSED_HEIGHT = 30
const PIP_MAX_MEASURED_HEIGHT = 320
const PIP_COLLAPSE_DELAY_MS = 400
const PIP_PILL_HEIGHT = 40
const PIP_MORPH_TRANSITION =
	'width 280ms ease, height 280ms ease, bottom 280ms ease, border-radius 280ms ease, opacity 280ms ease, transform 280ms ease'
const PIP_MORPH_DURATION_MS = 280
const PIP_SCROLL_HIDE_OFFSET = 100
const PIP_COLLAPSED_WIDTH_RATIO = 0.75
const PIP_SWIPE_THRESHOLD = 50
const PIP_SCROLL_DIRECTION_THRESHOLD = 40
const WHEEL_DELTA_LINE_MULTIPLIER = 15
const WHEEL_DELTA_PAGE_MULTIPLIER = 300
const PANEL_MIN_WIDTH = 320
const PANEL_MAX_WIDTH = 700
const RESIZE_NUB_WIDTH = 6
const EDGE_TAB_WIDTH = 44
const EDGE_TAB_HEIGHT = 48
const EDGE_TAB_DRAG_THRESHOLD_PX = 4

const ICON_MESSAGE = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>`
const ICON_CHEVRON_RIGHT = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>`

/**
 * Allowed positions for the collapsed PIP composer surface.
 *
 * @public
 */
export type CharPipPosition = 'bottom-center' | 'bottom-right' | 'bottom-left'

/**
 * Payload for `char-shell-open-change`.
 *
 * @public
 */
export interface CharShellOpenChangeDetail {
	open: boolean
}

type ShellHealthState = 'idle' | 'waiting' | 'healthy' | 'unavailable'
export type {
	CharContainerDimensions,
	CharDeviceCapabilities,
	CharDisplayMode,
	CharHostContext,
	CharHostStyles,
	CharPlatform,
	CharSafeAreaInsets,
	CharTheme,
	ConnectOptions,
} from './types'

function getPipPositionStyles(position: CharPipPosition): {
	left: string
	right: string
	transform: string
} {
	if (position === 'bottom-right') {
		return {
			left: 'auto',
			right: '16px',
			transform: 'none',
		}
	}
	if (position === 'bottom-left') {
		return {
			left: '16px',
			right: 'auto',
			transform: 'none',
		}
	}
	return {
		left: '50%',
		right: 'auto',
		transform: 'translateX(-50%)',
	}
}

function setStyleValue(el: HTMLElement, name: string, value: string): void {
	el.style.setProperty(name, value)
}

function resetStyleValue(el: HTMLElement, name: string): void {
	el.style.removeProperty(name)
}

function applyOpenAttribute(el: HTMLElement, open: boolean): void {
	if (open) {
		el.setAttribute('open', '')
		return
	}
	el.removeAttribute('open')
}

/**
 * Strips shell-owned mode fields from host context patches.
 * `char-agent-shell` owns `displayMode` and `availableDisplayModes`.
 */
function sanitizeShellHostContext(hostContext: CharHostContext): CharHostContext {
	const { displayMode: _displayMode, availableDisplayModes: _availableModes, ...rest } =
		hostContext
	return rest
}

/**
 * `<char-agent-shell>` custom element.
 *
 * Opinionated host shell that composes a persistent inner `<char-agent>` and
 * owns responsive mode policy plus host layout orchestration.
 * This shell does not render chat controls, prompts, or composer UI.
 * Visible assistant UI is always iframe-owned.
 *
 * Policy:
 * - closed to `pip`
 * - open desktop to `inline`
 * - open narrow viewport to `fullscreen`
 *
 * @public
 */
export class CharAgentShellElement extends HTMLElement {
	static observedAttributes = [
		'open',
		'enable-debug-tools',
		'api-base',
	]

	declare apiBase?: string

	private _agent: CharAgentElement | null = null
	private _open = false
	private _savedBodyTransition: string | null = null
	private _bodyMarginTimeoutId: number | null = null
	private _currentPanelWidth = PANEL_WIDTH
	private _resizeNubElement: HTMLDivElement | null = null
	private _edgeTabElement: HTMLButtonElement | null = null
	private _resizeDragging = false
	private _resizeDragOverlay: HTMLDivElement | null = null
	private _resizeStartX = 0
	private _resizeStartWidth = 0
	private _edgeTabDragStartX = 0
	private _edgeTabDragging = false
	private _isNarrowViewport = false
	private _mediaQuery: MediaQueryList | null = null
	private _mediaQueryHandler: ((event: MediaQueryListEvent) => void) | null = null
	private _themeObserver: MutationObserver | null = null
	private _themeMediaQuery: MediaQueryList | null = null
	private _themeMediaHandler: (() => void) | null = null
	private _pendingConnectOptions: ConnectOptions | null = null
	private _pendingHostContext: CharHostContext = {}
	private _isApplyingOpenAttribute = false
	private _forwardedListenersAttached = false
	private _healthState: ShellHealthState = 'idle'
	private _healthTimeoutId: number | null = null
	private _pipMeasuredHeight: number | null = null
	private _revealFrameId: number | null = null
	private _pipExpanded = false
	private _pipPillElement: HTMLDivElement | null = null
	private _pipCollapseTimerId: number | null = null
	private _hoverMediaQuery: MediaQueryList | null = null
	private _pipListenersAttached = false
	private _pipAnimating = false
	private _pipAnimationTimeoutId: number | null = null
	private _pendingPipMeasuredHeight: number | null = null
	private _tapOutsideListener: ((e: Event) => void) | null = null
	private _lastAppliedDisplayMode: CharDisplayMode | null = null
	private _pipHiddenByScroll = false
	private _scrollListener: ((e: Event) => void) | null = null
	private _scrollRafId: number | null = null
	private _lastScrollEl: Element | null = null
	private _lastScrollTop: number | null = null
	private _scrollDelta = 0
	private _swipeTouchStartY: number | null = null
	private _swipeStartAtTop = false
	private _swipeStartAtBottom = false
	private _swipeTouchStartListener: ((e: TouchEvent) => void) | null = null
	private _swipeTouchEndListener: ((e: TouchEvent) => void) | null = null
	private _wheelListener: ((e: WheelEvent) => void) | null = null
	private _wheelDelta = 0

	get open(): boolean {
		return this._open
	}

	set open(value: boolean) {
		this.setOpen(value)
	}

	connectedCallback(): void {
		this._upgradeProperty('open')
		this._upgradeProperty('apiBase')

		registerChar()
		this._ensureAgent()
		this._readConfigFromAttributes()
		this._syncForwardedAttributes()
		this._setupViewportObserver()
		this._setupThemeObserver()
		this._applyDisplayModeAndLayout()
		this._flushPendingConnectAndContext()
	}

	disconnectedCallback(): void {
		this._removeBodyMargin()
		if (this._bodyMarginTimeoutId !== null) {
			window.clearTimeout(this._bodyMarginTimeoutId)
			this._bodyMarginTimeoutId = null
		}
		this._removeResizeDragOverlay()
		this._teardownThemeObserver()
		document.removeEventListener('mousemove', this._onEdgeTabMouseMove)
		document.removeEventListener('mouseup', this._onEdgeTabMouseUp)
		document.removeEventListener('touchmove', this._onEdgeTabTouchMove)
		document.removeEventListener('touchend', this._onEdgeTabTouchEnd)
		this._removeEdgeTab()
		this._hideResizeNub()
		if (this._resizeNubElement) {
			this._resizeNubElement.remove()
			this._resizeNubElement = null
		}
		this._stopHealthWait()
		this._cancelRevealFade()
		this._teardownViewportObserver()
		this._detachForwardedListeners()
		this._detachPipInteractionListeners()
		this._clearPipCollapseTimer()
		if (this._pipAnimationTimeoutId !== null) {
			window.clearTimeout(this._pipAnimationTimeoutId)
			this._pipAnimationTimeoutId = null
		}
		this._pipAnimating = false
		if (this._pipPillElement) {
			this._pipPillElement.remove()
			this._pipPillElement = null
		}
		this._hoverMediaQuery = null
	}

	attributeChangedCallback(
		name: string,
		_oldValue: string | null,
		newValue: string | null,
	): void {
		switch (name) {
			case 'open': {
				if (this._isApplyingOpenAttribute) return
				const nextOpen = parseBooleanAttribute(newValue)
				this._setOpenInternal(nextOpen, {
					emit: true,
					reflect: false,
				})
				return
			}
			case 'enable-debug-tools':
			case 'api-base': {
				this._syncForwardedAttributes()
				return
			}
		}
	}

	/**
	 * Connects shell authentication and starts availability health checks.
	 *
	 * @param options - Authentication payload (`publishableKey` + optional `idToken`).
	 * @returns `true` when connect is accepted by the inner element.
	 */
	connect(options: ConnectOptions): boolean {
		if (this._healthState === 'unavailable') {
			this._recoverFromUnavailable()
		}

		if (!this._agent) {
			this._pendingConnectOptions = options
			this._startHealthWait()
			return true
		}

		const ok = this._agent.connect(options)
		if (ok) {
			this._pendingConnectOptions = options
			this._startHealthWait()
		}
		return ok
	}

	/**
	 * Clears authentication and disconnects the inner `<char-agent>`.
	 *
	 * @returns `true` when disconnect dispatch succeeds.
	 */
	disconnect(): boolean {
		this._pendingConnectOptions = null
		this._stopHealthWait()
		if (this._healthState !== 'unavailable') {
			this._healthState = 'idle'
		}
		return this._agent ? this._agent.disconnect() : true
	}

	/**
	 * Convenience method for declarative wrappers.
	 * Applies auth when provided, otherwise clears auth.
	 *
	 * @param options - Auth payload to connect, or `null` to disconnect.
	 * @returns Result from `connect()` or `disconnect()`.
	 */
	setAuth(options: ConnectOptions | null): boolean {
		if (!options) {
			return this.disconnect()
		}
		return this.connect(options)
	}

	/**
	 * Merges and forwards host context to the inner `<char-agent>`.
	 * Shell-owned fields (`displayMode`, `availableDisplayModes`) are ignored.
	 *
	 * @param hostContext - Partial host context patch to forward.
	 */
	setHostContext(hostContext: CharHostContext): void {
		const sanitizedContext = sanitizeShellHostContext(hostContext)
		if (Object.keys(sanitizedContext).length === 0) {
			return
		}
		if (!this._agent) {
			this._pendingHostContext = mergeHostContext(
				this._pendingHostContext,
				sanitizedContext,
			)
			return
		}
		this._agent.setHostContext(sanitizedContext)
	}

	/**
	 * Sets shell open state.
	 *
	 * @param open - Next desired open state.
	 */
	setOpen(open: boolean): void {
		if (this._healthState === 'unavailable' && open) {
			this.dispatchEvent(
				new CustomEvent('char-error', {
					bubbles: true,
					composed: true,
					detail: {
						code: 'SHELL_UNAVAILABLE',
						message: 'Cannot open shell: agent is unavailable',
					} satisfies CharErrorDetail,
				}),
			)
			return
		}
		this._setOpenInternal(Boolean(open), { emit: true, reflect: true })
	}

	/**
	 * Toggles shell open state.
	 */
	toggleOpen(): void {
		this._setOpenInternal(!this._open, { emit: true, reflect: true })
	}

	private _upgradeProperty(propertyName: string): void {
		if (!Object.hasOwn(this, propertyName)) {
			return
		}
		const value = (this as Record<string, unknown>)[propertyName]
		delete (this as Record<string, unknown>)[propertyName]
		;(this as Record<string, unknown>)[propertyName] = value
	}

	private _ensureAgent(): void {
		if (this._agent) {
			if (!this.contains(this._agent)) {
				this.appendChild(this._agent)
			}
			this._attachForwardedListeners()
			return
		}

		const agent = document.createElement('char-agent') as CharAgentElement
		agent.style.display = 'block'
		agent.style.width = '100%'
		agent.style.height = '100%'
		agent.style.border = 'none'
		this._primeAgentBootConfig(agent)
		this._agent = agent
		this.appendChild(agent)
		this._attachForwardedListeners()
	}

	private _primeAgentBootConfig(agent: CharAgentElement): void {
		for (const name of ['enable-debug-tools', 'api-base']) {
			const value = this.getAttribute(name)
			if (value !== null) {
				agent.setAttribute(name, value)
			}
		}

		agent.apiBase = typeof this.apiBase === 'string' ? this.apiBase : undefined
	}

	private _attachForwardedListeners(): void {
		if (!this._agent || this._forwardedListenersAttached) {
			return
		}
		this._agent.addEventListener(
			'char-request-display-mode',
			this._handleRequestDisplayMode,
		)
		this._agent.addEventListener('char-close', this._handleClose)
		this._agent.addEventListener('char-initialized', this._handleInitialized)
		this._agent.addEventListener('char-error', this._handleInnerError)
		this._agent.addEventListener('char-size-changed', this._handleInnerSizeChanged)
		this._forwardedListenersAttached = true
	}

	private _detachForwardedListeners(): void {
		if (!this._agent || !this._forwardedListenersAttached) {
			return
		}
		this._agent.removeEventListener(
			'char-request-display-mode',
			this._handleRequestDisplayMode,
		)
		this._agent.removeEventListener('char-close', this._handleClose)
		this._agent.removeEventListener('char-initialized', this._handleInitialized)
		this._agent.removeEventListener('char-error', this._handleInnerError)
		this._agent.removeEventListener('char-size-changed', this._handleInnerSizeChanged)
		this._forwardedListenersAttached = false
	}

	private _readConfigFromAttributes(): void {
		this._open = parseBooleanAttribute(this.getAttribute('open'))
	}

	private _syncForwardedAttributes(): void {
		if (!this._agent) return

		for (const name of ['enable-debug-tools', 'api-base']) {
			const value = this.getAttribute(name)
			if (value === null) {
				this._agent.removeAttribute(name)
			} else {
				this._agent.setAttribute(name, value)
			}
		}

		this._agent.apiBase = typeof this.apiBase === 'string' ? this.apiBase : undefined
	}

	private _setupViewportObserver(): void {
		if (typeof window === 'undefined') {
			return
		}
		const query = `(max-width: ${FULLSCREEN_BREAKPOINT - 1}px)`
		if (this._mediaQuery?.media === query) {
			this._isNarrowViewport = this._mediaQuery.matches
			return
		}

		this._teardownViewportObserver()
		this._mediaQuery = window.matchMedia(query)
		this._isNarrowViewport = this._mediaQuery.matches

		this._mediaQueryHandler = (event: MediaQueryListEvent) => {
			this._isNarrowViewport = event.matches
			this._applyDisplayModeAndLayout()
		}
		this._mediaQuery.addEventListener('change', this._mediaQueryHandler)
	}

	private _teardownViewportObserver(): void {
		if (this._mediaQuery && this._mediaQueryHandler) {
			this._mediaQuery.removeEventListener('change', this._mediaQueryHandler)
		}
		this._mediaQuery = null
		this._mediaQueryHandler = null
	}

	private _applyDisplayModeAndLayout(): void {
		if (!this._agent) return
		if (this._healthState === 'unavailable') {
			this._applyUnavailableLayout()
			return
		}
		if (this._healthState !== 'healthy') {
			this._applyPreReadyLayout()
			return
		}
		this.hidden = false

		const displayMode = resolvePolicyDisplayMode({
			open: this._open,
			isNarrowViewport: this._isNarrowViewport,
			availableModes: DEFAULT_AVAILABLE_DISPLAY_MODES,
		})
		if (displayMode !== 'pip') {
			if (this._pipMeasuredHeight !== null) {
				this._pipMeasuredHeight = null
			}
		}
		const prevMode = this._lastAppliedDisplayMode
		this._lastAppliedDisplayMode = displayMode
		this._agent.setAttribute('display-mode', displayMode)
		this._applyHostLayout(displayMode)
		this._applyAgentLayout(displayMode, prevMode)
	}

	private _isParentFlex(): boolean {
		const parent = this.parentElement
		if (!parent) return false
		const display = getComputedStyle(parent).display
		return display === 'flex' || display === 'inline-flex'
	}

	private _isDarkMode(): boolean {
		const themeAttr = document.documentElement.getAttribute('data-theme')
		if (themeAttr === 'dark') return true
		if (themeAttr === 'light') return false
		return (
			document.documentElement.classList.contains('dark') ||
			window.matchMedia('(prefers-color-scheme: dark)').matches
		)
	}

	/**
	 * Read a `--char-color-*` variable from the shell element. If the host
	 * page has set it (directly or via inheritance) use that value; otherwise
	 * fall back to the char design-system default for the current light/dark mode.
	 */
	private _resolveColor(
		token: keyof typeof THEME_LIGHT,
	): string {
		const varMap: Record<keyof typeof THEME_LIGHT, string> = {
			background: '--char-color-background',
			foreground: '--char-color-foreground',
			border: '--char-color-border',
			mutedForeground: '--char-color-muted-foreground',
			primary: '--char-color-primary',
		}
		const host = getComputedStyle(this).getPropertyValue(varMap[token]).trim()
		if (host) return host
		const palette = this._isDarkMode() ? THEME_DARK : THEME_LIGHT
		return palette[token]
	}

	private _panelBorder(): string {
		return `1px solid ${this._resolveColor('border')}`
	}

	private _setupThemeObserver(): void {
		const onThemeChange = (): void => {
			this._applyEdgeTabVisual()
			this._refreshResizeNubColors()
			if (this._open && !this._isNarrowViewport) {
				if (this._isParentFlex()) {
					setStyleValue(this, 'border-left', this._panelBorder())
				} else if (this._agent) {
					setStyleValue(this._agent, 'border-left', this._panelBorder())
				}
			}
		}

		this._themeObserver = new MutationObserver(onThemeChange)
		this._themeObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['class', 'style', 'data-theme'],
		})

		this._themeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
		this._themeMediaHandler = onThemeChange
		this._themeMediaQuery.addEventListener('change', this._themeMediaHandler)
	}

	private _teardownThemeObserver(): void {
		if (this._themeObserver) {
			this._themeObserver.disconnect()
			this._themeObserver = null
		}
		if (this._themeMediaQuery && this._themeMediaHandler) {
			this._themeMediaQuery.removeEventListener('change', this._themeMediaHandler)
			this._themeMediaQuery = null
			this._themeMediaHandler = null
		}
	}

	private _applyHostLayout(displayMode: CharDisplayMode): void {
		const useFlexLayout = this._isParentFlex()

		if (displayMode === 'inline' && !this._isNarrowViewport) {
			if (useFlexLayout) {
				setStyleValue(this, 'position', 'relative')
				setStyleValue(this, 'flex', `0 0 ${this._currentPanelWidth}px`)
				setStyleValue(this, 'width', `${this._currentPanelWidth}px`)
				setStyleValue(this, 'min-width', `${this._currentPanelWidth}px`)
				setStyleValue(this, 'height', '100%')
				setStyleValue(this, 'z-index', String(SHELL_Z_INDEX))
				setStyleValue(this, 'border-left', this._panelBorder())
				setStyleValue(
					this,
					'transition',
					this._resizeDragging ? 'none' : SHELL_LAYOUT_TRANSITION,
				)
				resetStyleValue(this, 'overflow')
				resetStyleValue(this, 'pointer-events')
				this._removeBodyMargin()
			} else {
				setStyleValue(this, 'position', 'fixed')
				setStyleValue(this, 'width', '0px')
				setStyleValue(this, 'height', '0px')
				setStyleValue(this, 'z-index', String(SHELL_Z_INDEX))
				resetStyleValue(this, 'flex')
				resetStyleValue(this, 'min-width')
				resetStyleValue(this, 'border-left')
				resetStyleValue(this, 'overflow')
				resetStyleValue(this, 'pointer-events')
				this._applyBodyMargin(this._currentPanelWidth)
			}
			this._showResizeNub()
			this._showEdgeTab()
			if (useFlexLayout) this._syncEdgeTabWithPanelTransition()
		} else if (displayMode === 'fullscreen') {
			if (useFlexLayout) {
				setStyleValue(this, 'position', 'relative')
				setStyleValue(this, 'flex', '0 0 0px')
				setStyleValue(this, 'width', '0px')
				setStyleValue(this, 'min-width', '0px')
				setStyleValue(this, 'height', '0px')
				setStyleValue(this, 'z-index', String(SHELL_Z_INDEX))
				resetStyleValue(this, 'border-left')
				setStyleValue(this, 'transition', SHELL_LAYOUT_TRANSITION)
				resetStyleValue(this, 'overflow')
				resetStyleValue(this, 'pointer-events')
			} else {
				setStyleValue(this, 'position', 'relative')
				resetStyleValue(this, 'flex')
				resetStyleValue(this, 'min-width')
				resetStyleValue(this, 'border-left')
				resetStyleValue(this, 'overflow')
				resetStyleValue(this, 'pointer-events')
			}
			this._removeBodyMargin()
			this._hideResizeNub()
			this._hideEdgeTab()
		} else {
			if (useFlexLayout) {
				setStyleValue(this, 'position', 'relative')
				setStyleValue(this, 'flex', '0 0 0px')
				setStyleValue(this, 'width', '0px')
				setStyleValue(this, 'min-width', '0px')
				setStyleValue(this, 'height', '0px')
				setStyleValue(this, 'z-index', String(SHELL_Z_INDEX))
				resetStyleValue(this, 'border-left')
				setStyleValue(this, 'transition', SHELL_LAYOUT_TRANSITION)
				resetStyleValue(this, 'overflow')
				resetStyleValue(this, 'pointer-events')
			} else {
				setStyleValue(this, 'position', 'relative')
				resetStyleValue(this, 'flex')
				resetStyleValue(this, 'min-width')
				resetStyleValue(this, 'border-left')
				resetStyleValue(this, 'overflow')
				resetStyleValue(this, 'pointer-events')
			}
			this._removeBodyMargin()
			this._hideResizeNub()
			this._showEdgeTab()
		}
	}

	private _applyBodyMargin(width: number): void {
		const body = document.body
		if (this._savedBodyTransition === null) {
			this._savedBodyTransition = body.style.transition || ''
		}
		if (!this._resizeDragging) {
			body.style.transition = BODY_MARGIN_TRANSITION
		}
		body.style.marginRight = `${width}px`
	}

	private _removeBodyMargin(): void {
		const body = document.body
		body.style.marginRight = ''
		if (this._bodyMarginTimeoutId !== null) {
			window.clearTimeout(this._bodyMarginTimeoutId)
			this._bodyMarginTimeoutId = null
		}
		if (this._savedBodyTransition !== null) {
			const saved = this._savedBodyTransition
			this._bodyMarginTimeoutId = window.setTimeout(() => {
				this._bodyMarginTimeoutId = null
				if (body.style.transition === BODY_MARGIN_TRANSITION) {
					body.style.transition = saved || ''
				}
			}, 220)
			this._savedBodyTransition = null
		}
	}

	private _ensureResizeNub(): HTMLDivElement {
		if (this._resizeNubElement) return this._resizeNubElement

		const nub = document.createElement('div')
		nub.setAttribute('role', 'separator')
		nub.setAttribute('aria-orientation', 'vertical')
		nub.setAttribute('aria-label', 'Resize panel')
		nub.style.cssText = [
			'position:fixed',
			'top:0',
			`width:${RESIZE_NUB_WIDTH}px`,
			'height:100dvh',
			'cursor:col-resize',
			`z-index:${SHELL_Z_INDEX + 1}`,
			'background:transparent',
			'user-select:none',
			'-webkit-user-select:none',
			'touch-action:none',
		].join(';')

		const indicator = document.createElement('div')
		indicator.style.cssText = [
			'position:absolute',
			'top:0',
			'left:50%',
			'transform:translateX(-50%)',
			'width:2px',
			'height:100%',
			'border-radius:1px',
			'transition:background 150ms ease, width 150ms ease',
			'pointer-events:none',
		].join(';')
		nub.appendChild(indicator)

		this._applyResizeNubColors(indicator)

		nub.addEventListener('mouseenter', () => {
			const dark = this._isDarkMode()
			indicator.style.background = dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'
			indicator.style.width = '3px'
		})
		nub.addEventListener('mouseleave', () => {
			if (!this._resizeDragging) {
				this._applyResizeNubColors(indicator)
				indicator.style.width = '2px'
			}
		})

		nub.addEventListener('mousedown', this._onResizeStart)
		nub.addEventListener('touchstart', this._onResizeTouchStart, { passive: false })

		this._resizeNubElement = nub
		return nub
	}

	private _applyResizeNubColors(indicator?: HTMLDivElement): void {
		const el = indicator ?? this._resizeNubElement?.querySelector('div') as HTMLDivElement | null
		if (!el) return
		const dark = this._isDarkMode()
		el.style.background = dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'
	}

	private _refreshResizeNubColors(): void {
		this._applyResizeNubColors()
	}

	private _showResizeNub(): void {
		const nub = this._ensureResizeNub()
		if (!nub.parentNode) {
			document.body.appendChild(nub)
		}
		const rightPos = this._currentPanelWidth - Math.floor(RESIZE_NUB_WIDTH / 2)
		setStyleValue(nub, 'right', `${rightPos}px`)
		setStyleValue(nub, 'display', 'block')
	}

	private _hideResizeNub(): void {
		if (this._resizeNubElement) {
			setStyleValue(this._resizeNubElement, 'display', 'none')
		}
	}

	private _ensureEdgeTab(): HTMLButtonElement {
		if (this._edgeTabElement) return this._edgeTabElement

		const tab = document.createElement('button')
		tab.setAttribute('type', 'button')
		tab.setAttribute('aria-label', 'Open assistant')
		tab.style.cssText = [
			'position:fixed',
			'top:50%',
			'right:0px',
			'transform:translateY(-50%)',
			`z-index:${SHELL_Z_INDEX + 2}`,
			`width:${EDGE_TAB_WIDTH}px`,
			`height:${EDGE_TAB_HEIGHT}px`,
			'display:flex',
			'align-items:center',
			'justify-content:center',
			'border-right:none',
			'border-radius:12px 0 0 12px',
			'padding:0',
			'outline:none',
			'transition:transform 200ms ease,background 150ms ease,color 150ms ease,box-shadow 200ms ease,border-color 150ms ease',
			'user-select:none',
			'-webkit-user-select:none',
		].join(';')

		const dot = document.createElement('span')
		dot.style.cssText = [
			'position:absolute',
			'top:8px',
			'right:10px',
			'width:7px',
			'height:7px',
			'border-radius:50%',
			'transition:opacity 200ms ease',
		].join(';')
		tab.appendChild(dot)

		const icon = document.createElement('span')
		icon.style.cssText = 'display:flex;align-items:center;justify-content:center;pointer-events:none;'
		icon.innerHTML = ICON_MESSAGE
		tab.appendChild(icon)

		tab.addEventListener('mouseenter', () => {
			const dark = this._isDarkMode()
			if (this._open && !this._isNarrowViewport) {
				tab.style.background = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'
				tab.style.boxShadow = 'none'
				tab.style.color = this._resolveColor('foreground')
				tab.style.cursor = 'col-resize'
			} else {
				tab.style.transform = 'translateY(-50%) translateX(-3px)'
				tab.style.boxShadow = dark
					? '-4px 0 16px rgba(0,0,0,0.3)'
					: '-4px 0 16px rgba(0,0,0,0.12)'
				tab.style.color = this._resolveColor('foreground')
				tab.style.cursor = 'pointer'
			}
		})
		tab.addEventListener('mouseleave', () => {
			this._applyEdgeTabVisual()
		})

		tab.addEventListener('mousedown', this._onEdgeTabMouseDown)
		tab.addEventListener('touchstart', this._onEdgeTabTouchStart, { passive: false })

		this._edgeTabElement = tab
		return tab
	}

	/** Apply visual style (colors, shadow) based on open state — no position change. */
	private _applyEdgeTabVisual(): void {
		if (!this._edgeTabElement) return
		const tab = this._edgeTabElement
		const icon = tab.querySelector('span:last-child') as HTMLSpanElement | null
		const dot = tab.querySelector('span:first-child') as HTMLSpanElement | null
		const dark = this._isDarkMode()
		const bg = this._resolveColor('background')
		const borderColor = this._resolveColor('border')
		const mutedFg = this._resolveColor('mutedForeground')
		const primary = this._resolveColor('primary')

		tab.style.border = `1px solid ${borderColor}`
		tab.style.borderRight = 'none'

		if (this._open && !this._isNarrowViewport) {
			tab.setAttribute('aria-label', 'Close assistant')
			tab.setAttribute('aria-expanded', 'true')
			if (icon) icon.innerHTML = ICON_CHEVRON_RIGHT
			if (dot) dot.style.opacity = '0'
			tab.style.background = bg
			tab.style.boxShadow = 'none'
			tab.style.backdropFilter = 'none'
			tab.style.setProperty('-webkit-backdrop-filter', 'none')
			tab.style.color = mutedFg
			tab.style.cursor = 'col-resize'
			tab.style.transform = 'translateY(-50%)'
		} else {
			tab.setAttribute('aria-label', 'Open assistant')
			tab.setAttribute('aria-expanded', 'false')
			if (icon) icon.innerHTML = ICON_MESSAGE
			if (dot) {
				dot.style.opacity = '1'
				dot.style.background = primary
			}
			tab.style.background = dark
				? 'color-mix(in oklch, ' + bg + ', white 4%)'
				: 'color-mix(in oklch, ' + bg + ', transparent 5%)'
			tab.style.boxShadow = dark
				? '-2px 0 12px rgba(0,0,0,0.25)'
				: '-2px 0 12px rgba(0,0,0,0.08)'
			tab.style.backdropFilter = 'blur(8px)'
			tab.style.setProperty('-webkit-backdrop-filter', 'blur(8px)')
			tab.style.color = mutedFg
			tab.style.cursor = 'pointer'
			tab.style.transform = 'translateY(-50%)'
		}
	}

	/** Set the tab's `right` position immediately (no transition). */
	private _positionEdgeTab(rightPx: number): void {
		if (this._edgeTabElement) {
			this._edgeTabElement.style.right = `${rightPx}px`
		}
	}

	private _showEdgeTab(): void {
		const tab = this._ensureEdgeTab()
		if (!tab.parentNode) {
			document.body.appendChild(tab)
		}
		setStyleValue(tab, 'display', 'flex')

		if (this._open && !this._isNarrowViewport) {
			this._positionEdgeTab(this._currentPanelWidth - 1)
		} else {
			this._positionEdgeTab(0)
		}
		this._applyEdgeTabVisual()
	}

	private _hideEdgeTab(): void {
		if (this._edgeTabElement) {
			setStyleValue(this._edgeTabElement, 'display', 'none')
		}
	}

	private _removeEdgeTab(): void {
		if (this._edgeTabElement) {
			this._edgeTabElement.remove()
			this._edgeTabElement = null
		}
	}

	private _onEdgeTabMouseDown = (e: MouseEvent): void => {
		e.preventDefault()
		this._edgeTabDragStartX = e.clientX
		this._edgeTabDragging = false
		document.addEventListener('mousemove', this._onEdgeTabMouseMove)
		document.addEventListener('mouseup', this._onEdgeTabMouseUp)
	}

	private _onEdgeTabTouchStart = (e: TouchEvent): void => {
		if (e.touches.length !== 1) return
		e.preventDefault()
		this._edgeTabDragStartX = e.touches[0].clientX
		this._edgeTabDragging = false
		document.addEventListener('touchmove', this._onEdgeTabTouchMove, { passive: false })
		document.addEventListener('touchend', this._onEdgeTabTouchEnd)
	}

	private _onEdgeTabMouseMove = (e: MouseEvent): void => {
		const dx = Math.abs(e.clientX - this._edgeTabDragStartX)
		if (!this._edgeTabDragging && dx >= EDGE_TAB_DRAG_THRESHOLD_PX) {
			this._edgeTabDragging = true
			this._promoteEdgeTabDragToResize(this._edgeTabDragStartX)
		}
	}

	private _onEdgeTabTouchMove = (e: TouchEvent): void => {
		if (e.touches.length !== 1) return
		e.preventDefault()
		const dx = Math.abs(e.touches[0].clientX - this._edgeTabDragStartX)
		if (!this._edgeTabDragging && dx >= EDGE_TAB_DRAG_THRESHOLD_PX) {
			this._edgeTabDragging = true
			this._promoteEdgeTabDragToResize(this._edgeTabDragStartX)
		}
	}

	private _onEdgeTabMouseUp = (_e: MouseEvent): void => {
		document.removeEventListener('mousemove', this._onEdgeTabMouseMove)
		document.removeEventListener('mouseup', this._onEdgeTabMouseUp)
		if (!this._edgeTabDragging) {
			this.toggleOpen()
		}
	}

	private _onEdgeTabTouchEnd = (_e: TouchEvent): void => {
		document.removeEventListener('touchmove', this._onEdgeTabTouchMove)
		document.removeEventListener('touchend', this._onEdgeTabTouchEnd)
		if (!this._edgeTabDragging) {
			this.toggleOpen()
		}
	}

	/**
	 * Once the edge tab drag crosses the threshold, hand off to the existing
	 * resize machinery so the panel tracks the pointer smoothly.
	 */
	private _promoteEdgeTabDragToResize(startX: number): void {
		if (!this._open || this._isNarrowViewport) {
			this._edgeTabDragging = false
			return
		}
		this._resizeDragging = true
		this._resizeStartX = startX
		this._resizeStartWidth = this._currentPanelWidth
		this._showResizeDragOverlay()
		if (this._agent) setStyleValue(this._agent, 'transition', 'none')
		setStyleValue(this, 'transition', 'none')
		document.body.style.transition = 'none'
		document.body.style.cursor = 'col-resize'
		document.body.style.userSelect = 'none'

		document.addEventListener('mousemove', this._onResizeMove)
		document.addEventListener('mouseup', this._onResizeEnd)
		document.addEventListener('touchmove', this._onResizeTouchMove, { passive: false })
		document.addEventListener('touchend', this._onResizeTouchEnd)
	}

	/**
	 * Run a rAF loop that reads the shell element's actual rendered width
	 * and pins the edge tab to it. Used during flex layout transitions so
	 * the tab tracks the animated flex-basis exactly, frame by frame.
	 */
	private _syncEdgeTabWithPanelTransition(): void {
		if (!this._edgeTabElement) return
		const start = performance.now()
		const maxDuration = 300 // slightly longer than 220ms transition
		const tick = (): void => {
			if (!this._edgeTabElement || !this._open) return
			if (this._isParentFlex()) {
				const width = this.getBoundingClientRect().width
				this._positionEdgeTab(width - 1)
			}
			if (performance.now() - start < maxDuration) {
				requestAnimationFrame(tick)
			}
		}
		requestAnimationFrame(tick)
	}

	private _showResizeDragOverlay(): void {
		if (this._resizeDragOverlay) return
		const overlay = document.createElement('div')
		overlay.style.cssText = [
			'position:fixed',
			'top:0',
			'left:0',
			'width:100vw',
			'height:100vh',
			'cursor:col-resize',
			`z-index:${SHELL_Z_INDEX + 1}`,
			'background:transparent',
			'user-select:none',
			'-webkit-user-select:none',
		].join(';')
		document.body.appendChild(overlay)
		this._resizeDragOverlay = overlay
	}

	private _removeResizeDragOverlay(): void {
		if (this._resizeDragOverlay) {
			this._resizeDragOverlay.remove()
			this._resizeDragOverlay = null
		}
	}

	private _onResizeStart = (e: MouseEvent): void => {
		e.preventDefault()
		this._resizeDragging = true
		this._resizeStartX = e.clientX
		this._resizeStartWidth = this._currentPanelWidth

		this._showResizeDragOverlay()

		if (this._agent) {
			setStyleValue(this._agent, 'transition', 'none')
		}
		setStyleValue(this, 'transition', 'none')
		document.body.style.transition = 'none'
		document.body.style.cursor = 'col-resize'
		document.body.style.userSelect = 'none'

		document.addEventListener('mousemove', this._onResizeMove)
		document.addEventListener('mouseup', this._onResizeEnd)
	}

	private _onResizeTouchStart = (e: TouchEvent): void => {
		if (e.touches.length !== 1) return
		e.preventDefault()
		const touch = e.touches[0]
		this._resizeDragging = true
		this._resizeStartX = touch.clientX
		this._resizeStartWidth = this._currentPanelWidth

		this._showResizeDragOverlay()

		if (this._agent) {
			setStyleValue(this._agent, 'transition', 'none')
		}
		setStyleValue(this, 'transition', 'none')
		document.body.style.transition = 'none'

		document.addEventListener('touchmove', this._onResizeTouchMove, { passive: false })
		document.addEventListener('touchend', this._onResizeTouchEnd)
	}

	private _onResizeMove = (e: MouseEvent): void => {
		if (!this._resizeDragging) return
		const delta = this._resizeStartX - e.clientX
		this._applyResizeWidth(this._resizeStartWidth + delta)
	}

	private _onResizeTouchMove = (e: TouchEvent): void => {
		if (!this._resizeDragging || e.touches.length !== 1) return
		e.preventDefault()
		const delta = this._resizeStartX - e.touches[0].clientX
		this._applyResizeWidth(this._resizeStartWidth + delta)
	}

	private _applyResizeWidth(rawWidth: number): void {
		const width = Math.max(PANEL_MIN_WIDTH, Math.min(rawWidth, PANEL_MAX_WIDTH))
		this._currentPanelWidth = width

		const useFlexLayout = this._isParentFlex()
		if (useFlexLayout) {
			setStyleValue(this, 'flex', `0 0 ${width}px`)
			setStyleValue(this, 'width', `${width}px`)
			setStyleValue(this, 'min-width', `${width}px`)
		} else {
			if (this._agent) {
				setStyleValue(this._agent, 'width', `${width}px`)
			}
			document.body.style.marginRight = `${width}px`
		}
		if (this._resizeNubElement) {
			const rightPos = width - Math.floor(RESIZE_NUB_WIDTH / 2)
			setStyleValue(this._resizeNubElement, 'right', `${rightPos}px`)
		}
		if (this._edgeTabElement) {
			this._edgeTabElement.style.right = `${width - 1}px`
		}
	}

	private _onResizeEnd = (e: MouseEvent): void => {
		this._finishResize(e.clientX)
		document.removeEventListener('mousemove', this._onResizeMove)
		document.removeEventListener('mouseup', this._onResizeEnd)
	}

	private _onResizeTouchEnd = (e: TouchEvent): void => {
		const x = e.changedTouches[0]?.clientX ?? this._resizeStartX
		this._finishResize(x)
		document.removeEventListener('touchmove', this._onResizeTouchMove)
		document.removeEventListener('touchend', this._onResizeTouchEnd)
	}

	private _finishResize(endX: number): void {
		this._resizeDragging = false
		this._removeResizeDragOverlay()
		document.body.style.cursor = ''
		document.body.style.userSelect = ''
		document.body.style.transition = ''

		const delta = this._resizeStartX - endX
		const finalWidth = this._resizeStartWidth + delta

		this._currentPanelWidth = Math.max(PANEL_MIN_WIDTH, Math.min(finalWidth, PANEL_MAX_WIDTH))
		this._applyDisplayModeAndLayout()
	}

	private _composePipTransform(opts: {
		scrollHide?: boolean
	}): string {
		const parts: string[] = []
		if (PIP_POSITION === 'bottom-center') parts.push('translateX(-50%)')
		if (opts.scrollHide) parts.push(`translateY(${PIP_SCROLL_HIDE_OFFSET}px)`)
		return parts.length > 0 ? parts.join(' ') : 'none'
	}

	private _applyAgentLayout(
		displayMode: CharDisplayMode,
		prevMode?: CharDisplayMode | null,
	): void {
		if (!this._agent) return

		if (displayMode === 'inline' && !this._isNarrowViewport) {
			this._detachPipInteractionListeners()
			this._clearPipCollapseTimer()
			this._pipExpanded = false
			this._pipAnimating = false
			this._hidePipPill()

			const useFlexLayout = this._isParentFlex()
			if (useFlexLayout) {
				setStyleValue(this._agent, 'position', 'relative')
				resetStyleValue(this._agent, 'top')
				resetStyleValue(this._agent, 'right')
				resetStyleValue(this._agent, 'bottom')
				resetStyleValue(this._agent, 'left')
				setStyleValue(this._agent, 'z-index', 'auto')
				setStyleValue(this._agent, 'width', '100%')
				setStyleValue(this._agent, 'height', '100%')
			} else {
				setStyleValue(this._agent, 'position', 'fixed')
				setStyleValue(this._agent, 'top', '0')
				setStyleValue(this._agent, 'right', '0')
				resetStyleValue(this._agent, 'bottom')
				resetStyleValue(this._agent, 'left')
				setStyleValue(this._agent, 'z-index', String(SHELL_Z_INDEX))
				setStyleValue(this._agent, 'width', `${this._currentPanelWidth}px`)
				setStyleValue(this._agent, 'height', '100dvh')
				setStyleValue(this._agent, 'border-left', this._panelBorder())
			}
			resetStyleValue(this._agent, 'transform')
			resetStyleValue(this._agent, 'max-width')
			resetStyleValue(this._agent, 'max-height')
			setStyleValue(this._agent, 'border-radius', '0')
			setStyleValue(this._agent, 'overflow', 'hidden')
			setStyleValue(this._agent, 'transition', this._resizeDragging ? 'none' : 'width 220ms ease')
			resetStyleValue(this._agent, 'opacity')
			resetStyleValue(this._agent, 'pointer-events')
			return
		}

		if (displayMode === 'fullscreen') {
			this._detachPipInteractionListeners()
			this._clearPipCollapseTimer()
			this._pipExpanded = false
			this._pipAnimating = false
			this._hidePipPill()
			setStyleValue(this._agent, 'position', 'fixed')
			setStyleValue(this._agent, 'top', '0')
			setStyleValue(this._agent, 'right', '0')
			setStyleValue(this._agent, 'bottom', '0')
			setStyleValue(this._agent, 'left', '0')
			resetStyleValue(this._agent, 'transform')
			resetStyleValue(this._agent, 'max-width')
			resetStyleValue(this._agent, 'max-height')
			setStyleValue(this._agent, 'z-index', String(SHELL_Z_INDEX))
			setStyleValue(this._agent, 'width', '100vw')
			setStyleValue(this._agent, 'height', '100dvh')
			setStyleValue(this._agent, 'border-radius', '0')
			setStyleValue(this._agent, 'overflow', 'hidden')
			resetStyleValue(this._agent, 'transition')
			resetStyleValue(this._agent, 'opacity')
			resetStyleValue(this._agent, 'pointer-events')
			return
		}

		const pipPositionStyles = getPipPositionStyles(PIP_POSITION)
		const expandedWidth = Math.max(PIP_MIN_COLLAPSED_WIDTH, PIP_WIDTH)
		const collapsedWidth = Math.max(
			PIP_MIN_COLLAPSED_WIDTH,
			Math.round(PIP_WIDTH * PIP_COLLAPSED_WIDTH_RATIO),
		)
		const targetWidth = this._pipExpanded ? expandedWidth : collapsedWidth
		const collapsedHeight = Math.max(PIP_MIN_COLLAPSED_HEIGHT, PIP_HEIGHT)
		const measuredHeightFloor =
			typeof this._pipMeasuredHeight === 'number' ? this._pipMeasuredHeight : 0
		const expandedHeight = Math.max(collapsedHeight, measuredHeightFloor)
		const collapsedPipHeight = PIP_PILL_HEIGHT
		const heightDelta = expandedHeight - collapsedPipHeight

		let targetHeight: number
		let targetBottom: string

		if (this._pipExpanded) {
			targetHeight = expandedHeight
			targetBottom = SAFE_BOTTOM
		} else {
			targetHeight = collapsedPipHeight
			targetBottom = heightDelta > 0
				? `calc(${heightDelta}px + 16px + env(safe-area-inset-bottom, 0px))`
				: SAFE_BOTTOM
		}

		const enteringFromPanel =
			prevMode != null && prevMode !== 'pip' && this._healthState === 'healthy'

		const composedTransform = this._composePipTransform({
			scrollHide: this._pipHiddenByScroll,
		})

		if (enteringFromPanel) {
			setStyleValue(this._agent, 'transition', 'none')
		}

		setStyleValue(this._agent, 'position', 'fixed')
		resetStyleValue(this._agent, 'top')
		setStyleValue(this._agent, 'right', pipPositionStyles.right)
		setStyleValue(this._agent, 'left', pipPositionStyles.left)
		setStyleValue(this._agent, 'bottom', targetBottom)
		setStyleValue(this._agent, 'z-index', String(SHELL_Z_INDEX))
		setStyleValue(
			this._agent,
			'width',
			`min(${targetWidth}px, calc(100vw - 24px))`,
		)
		setStyleValue(this._agent, 'height', `${targetHeight}px`)
		setStyleValue(
			this._agent,
			'max-height',
			'calc(100dvh - 24px - env(safe-area-inset-bottom, 0px))',
		)
		setStyleValue(this._agent, 'max-width', '100vw')
		setStyleValue(this._agent, 'border-radius', '16px')
		setStyleValue(this._agent, 'overflow', 'hidden')
		resetStyleValue(this._agent, 'border-left')

		if (enteringFromPanel) {
			setStyleValue(
				this._agent,
				'transform',
				this._composePipTransform({ scrollHide: true }),
			)
			setStyleValue(this._agent, 'opacity', '0')
			void this._agent.offsetHeight
			requestAnimationFrame(() => {
				if (!this._agent) return
				setStyleValue(this._agent, 'transition', PIP_MORPH_TRANSITION)
				setStyleValue(this._agent, 'transform', composedTransform)
				setStyleValue(this._agent, 'opacity', '1')
			})
		} else {
			setStyleValue(this._agent, 'transform', composedTransform)
			setStyleValue(
				this._agent,
				'transition',
				this._healthState === 'healthy' ? PIP_MORPH_TRANSITION : 'none',
			)
			resetStyleValue(this._agent, 'opacity')
		}

		if (this._healthState === 'healthy') {
			const pill = this._ensurePipPill()
			if (!pill.parentNode) {
				this.appendChild(pill)
			}
			this._updatePipPillPosition(collapsedWidth, heightDelta, collapsedPipHeight)
			this._attachPipInteractionListeners()
			this._attachScrollHideListener()
			this._attachSwipeGestureListeners()
			this._attachWheelFallbackListener()

			if (enteringFromPanel) {
				pill.style.opacity = '0'
				pill.style.pointerEvents = 'none'
				pill.style.transition = 'none'
				requestAnimationFrame(() => {
					pill.style.transition = PIP_MORPH_TRANSITION
					pill.style.opacity = '1'
					pill.style.pointerEvents = ''
				})
			}

			this._applyPipVisualState()
		}
	}

	private _ensurePipPill(): HTMLDivElement {
		if (this._pipPillElement) return this._pipPillElement

		const pill = document.createElement('div')
		pill.setAttribute('role', 'button')
		pill.setAttribute('aria-label', 'Open assistant')
		pill.setAttribute('tabindex', '0')

		const pipPositionStyles = getPipPositionStyles(PIP_POSITION)
		pill.style.cssText = [
			'position:fixed',
			`left:${pipPositionStyles.left}`,
			`right:${pipPositionStyles.right}`,
			`z-index:${SHELL_Z_INDEX + 1}`,
			'background:transparent',
			'border-radius:16px',
			'cursor:pointer',
			'user-select:none',
			'-webkit-user-select:none',
		].join(';')

		this._pipPillElement = pill
		return pill
	}

	private _updatePipPillPosition(pipWidth: number, heightDelta: number, pillHeight: number): void {
		if (!this._pipPillElement) return
		const collapsedBottom = heightDelta > 0
			? `calc(${heightDelta}px + 16px + env(safe-area-inset-bottom, 0px))`
			: SAFE_BOTTOM
		setStyleValue(this._pipPillElement, 'bottom', collapsedBottom)
		setStyleValue(this._pipPillElement, 'width', `min(${pipWidth}px, calc(100vw - 24px))`)
		setStyleValue(this._pipPillElement, 'height', `${pillHeight}px`)
		setStyleValue(
			this._pipPillElement,
			'transform',
			this._composePipTransform({
				scrollHide: this._pipHiddenByScroll,
			}),
		)
		setStyleValue(
			this._pipPillElement,
			'transition',
			'transform 280ms ease, width 280ms ease, opacity 200ms ease',
		)
	}

	private _applyPipVisualState(): void {
		if (!this._agent || !this._pipPillElement) return

		if (this._pipHiddenByScroll) {
			setStyleValue(this._agent, 'opacity', '0')
			setStyleValue(this._agent, 'pointer-events', 'none')
			setStyleValue(this._pipPillElement, 'opacity', '0')
			setStyleValue(this._pipPillElement, 'pointer-events', 'none')
			return
		}

		setStyleValue(this._agent, 'opacity', '1')
		if (this._pipExpanded) {
			setStyleValue(this._agent, 'pointer-events', 'auto')
			setStyleValue(this._pipPillElement, 'pointer-events', 'none')
		} else {
			setStyleValue(this._agent, 'pointer-events', 'none')
			setStyleValue(this._pipPillElement, 'pointer-events', 'auto')
		}
	}

	private _setPipExpanded(expanded: boolean): void {
		this._pipExpanded = expanded
		this._clearPipCollapseTimer()
		this._applyPipVisualState()

		if (this._agent?.getAttribute('display-mode') === 'pip') {
			this._applyAgentLayout('pip')
		}

		this._pipAnimating = true
		this._pendingPipMeasuredHeight = null
		if (this._pipAnimationTimeoutId !== null) {
			window.clearTimeout(this._pipAnimationTimeoutId)
		}
		this._pipAnimationTimeoutId = window.setTimeout(() => {
			this._pipAnimating = false
			this._pipAnimationTimeoutId = null
			this._flushPendingPipMeasuredHeight()
		}, PIP_MORPH_DURATION_MS)

		if (expanded) {
			this._attachTapOutsideListener()
		} else {
			this._detachTapOutsideListener()
		}
	}

	private _flushPendingPipMeasuredHeight(): void {
		const pending = this._pendingPipMeasuredHeight
		this._pendingPipMeasuredHeight = null
		if (pending === null || pending === this._pipMeasuredHeight) return
		this._pipMeasuredHeight = pending
		if (this._agent?.getAttribute('display-mode') === 'pip') {
			this._applyAgentLayout('pip')
		}
	}

	private _isHoverDevice(): boolean {
		if (!this._hoverMediaQuery) {
			if (typeof window === 'undefined') return false
			this._hoverMediaQuery = window.matchMedia('(hover: hover)')
		}
		return this._hoverMediaQuery.matches
	}

	private _attachPipInteractionListeners(): void {
		if (this._pipListenersAttached || !this._agent || !this._pipPillElement) return
		this._pipListenersAttached = true
		const pill = this._pipPillElement
		const agent = this._agent

		if (this._isHoverDevice()) {
			pill.addEventListener('pointerenter', this._handlePipPointerEnter)
			agent.addEventListener('pointerenter', this._handlePipPointerEnter)
			pill.addEventListener('pointerleave', this._handlePipPointerLeave)
			agent.addEventListener('pointerleave', this._handlePipPointerLeave)
		}

		pill.addEventListener('click', this._handlePillClick)
		pill.addEventListener('keydown', this._handlePillKeydown)
	}

	private _detachPipInteractionListeners(): void {
		if (!this._pipListenersAttached) return
		this._pipListenersAttached = false
		const pill = this._pipPillElement
		const agent = this._agent

		if (pill) {
			pill.removeEventListener('pointerenter', this._handlePipPointerEnter)
			pill.removeEventListener('pointerleave', this._handlePipPointerLeave)
			pill.removeEventListener('click', this._handlePillClick)
			pill.removeEventListener('keydown', this._handlePillKeydown)
		}
		if (agent) {
			agent.removeEventListener('pointerenter', this._handlePipPointerEnter)
			agent.removeEventListener('pointerleave', this._handlePipPointerLeave)
		}
		this._detachTapOutsideListener()
		this._detachScrollHideListener()
		this._detachSwipeGestureListeners()
		this._detachWheelFallbackListener()
	}

	private _schedulePipCollapse(): void {
		this._clearPipCollapseTimer()
		this._pipCollapseTimerId = window.setTimeout(() => {
			this._pipCollapseTimerId = null
			this._setPipExpanded(false)
		}, PIP_COLLAPSE_DELAY_MS)
	}

	private _clearPipCollapseTimer(): void {
		if (this._pipCollapseTimerId !== null) {
			window.clearTimeout(this._pipCollapseTimerId)
			this._pipCollapseTimerId = null
		}
	}

	private _attachTapOutsideListener(): void {
		if (this._tapOutsideListener || this._isHoverDevice()) return
		this._tapOutsideListener = (e: Event) => {
			const target = e.target as Node | null
			if (!target) return
			if (this._pipPillElement?.contains(target)) return
			if (this._agent?.contains(target)) return
			this._setPipExpanded(false)
		}
		document.addEventListener('click', this._tapOutsideListener, true)
	}

	private _detachTapOutsideListener(): void {
		if (this._tapOutsideListener) {
			document.removeEventListener('click', this._tapOutsideListener, true)
			this._tapOutsideListener = null
		}
	}

	private _handlePipPointerEnter = (): void => {
		this._clearPipCollapseTimer()
		if (!this._pipExpanded) {
			this._setPipExpanded(true)
		}
	}

	private _handlePipPointerLeave = (): void => {
		if (this._pipExpanded) {
			this._schedulePipCollapse()
		}
	}

	private _attachScrollHideListener(): void {
		if (this._scrollListener || typeof window === 'undefined') return

		this._scrollListener = (e: Event) => {
			const target = e.target
			let scrollEl: Element | null = null
			if (target === document || target === document.documentElement) {
				scrollEl = document.scrollingElement || document.documentElement
			} else if (target instanceof Element) {
				scrollEl = target
			}
			if (!scrollEl) return

			if (scrollEl.clientHeight < window.innerHeight * 0.3) return

			if (this._scrollRafId !== null) return
			this._scrollRafId = requestAnimationFrame(() => {
				this._scrollRafId = null
				this._evaluateScrollPosition(scrollEl!)
			})
		}

		document.addEventListener('scroll', this._scrollListener, {
			capture: true,
			passive: true,
		} as EventListenerOptions)
	}

	private _evaluateScrollPosition(el: Element): void {
		const scrollTop = el.scrollTop
		const maxScroll = el.scrollHeight - el.clientHeight

		if (el !== this._lastScrollEl) {
			this._lastScrollEl = el
			this._lastScrollTop = scrollTop
			this._scrollDelta = 0
			return
		}

		if (scrollTop <= 0) {
			this._lastScrollTop = 0
			this._scrollDelta = 0
			if (this._pipHiddenByScroll) {
				this._pipHiddenByScroll = false
				if (this._agent?.getAttribute('display-mode') === 'pip') {
					this._applyPipVisualState()
					this._applyAgentLayout('pip')
				}
			}
			return
		}

		if (maxScroll > 0 && scrollTop >= maxScroll - 2) {
			this._lastScrollTop = scrollTop
			this._scrollDelta = 0
			if (!this._pipHiddenByScroll) {
				this._pipHiddenByScroll = true
				if (this._agent?.getAttribute('display-mode') === 'pip') {
					this._applyPipVisualState()
					this._applyAgentLayout('pip')
				}
			}
			return
		}

		if (this._lastScrollTop === null) {
			this._lastScrollTop = scrollTop
			return
		}

		const delta = scrollTop - this._lastScrollTop
		this._lastScrollTop = scrollTop

		if ((delta > 0 && this._scrollDelta < 0) || (delta < 0 && this._scrollDelta > 0)) {
			this._scrollDelta = 0
		}
		this._scrollDelta += delta

		if (this._scrollDelta > PIP_SCROLL_DIRECTION_THRESHOLD && !this._pipHiddenByScroll) {
			this._pipHiddenByScroll = true
			this._scrollDelta = 0
			if (this._agent?.getAttribute('display-mode') === 'pip') {
				this._applyPipVisualState()
				this._applyAgentLayout('pip')
			}
		} else if (this._scrollDelta < -PIP_SCROLL_DIRECTION_THRESHOLD && this._pipHiddenByScroll) {
			this._pipHiddenByScroll = false
			this._scrollDelta = 0
			if (this._agent?.getAttribute('display-mode') === 'pip') {
				this._applyPipVisualState()
				this._applyAgentLayout('pip')
			}
		}
	}

	private _detachScrollHideListener(): void {
		if (this._scrollListener) {
			document.removeEventListener('scroll', this._scrollListener, true)
			this._scrollListener = null
		}
		if (this._scrollRafId !== null) {
			cancelAnimationFrame(this._scrollRafId)
			this._scrollRafId = null
		}
		this._pipHiddenByScroll = false
		this._lastScrollEl = null
		this._lastScrollTop = null
		this._scrollDelta = 0
	}

	private _attachSwipeGestureListeners(): void {
		if (this._swipeTouchStartListener || typeof window === 'undefined') return

		this._swipeTouchStartListener = (e: TouchEvent) => {
			if (e.touches.length !== 1) return
			this._swipeTouchStartY = e.touches[0].clientY
			const scrollEl = document.scrollingElement || document.documentElement
			this._swipeStartAtTop = scrollEl.scrollTop <= 0
			this._swipeStartAtBottom =
				scrollEl.scrollHeight <= scrollEl.clientHeight ||
				scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 1
		}

		this._swipeTouchEndListener = (e: TouchEvent) => {
			if (this._swipeTouchStartY === null) return
			if (e.changedTouches.length === 0) return
			const endY = e.changedTouches[0].clientY
			const deltaY = endY - this._swipeTouchStartY
			this._swipeTouchStartY = null

			if (Math.abs(deltaY) < PIP_SWIPE_THRESHOLD) return

			if (deltaY < -PIP_SWIPE_THRESHOLD && this._swipeStartAtTop && this._pipHiddenByScroll) {
				this._pipHiddenByScroll = false
				if (this._agent?.getAttribute('display-mode') === 'pip') {
					this._applyPipVisualState()
					this._applyAgentLayout('pip')
				}
				return
			}

			if (deltaY > PIP_SWIPE_THRESHOLD && this._swipeStartAtBottom && !this._pipHiddenByScroll) {
				this._pipHiddenByScroll = true
				if (this._agent?.getAttribute('display-mode') === 'pip') {
					this._applyPipVisualState()
					this._applyAgentLayout('pip')
				}
			}
		}

		document.addEventListener('touchstart', this._swipeTouchStartListener, {
			capture: true,
			passive: true,
		} as EventListenerOptions)
		document.addEventListener('touchend', this._swipeTouchEndListener, {
			capture: true,
			passive: true,
		} as EventListenerOptions)
	}

	private _detachSwipeGestureListeners(): void {
		if (this._swipeTouchStartListener) {
			document.removeEventListener('touchstart', this._swipeTouchStartListener, true)
			this._swipeTouchStartListener = null
		}
		if (this._swipeTouchEndListener) {
			document.removeEventListener('touchend', this._swipeTouchEndListener, true)
			this._swipeTouchEndListener = null
		}
		this._swipeTouchStartY = null
	}

	private _attachWheelFallbackListener(): void {
		if (this._wheelListener || typeof window === 'undefined') return

		this._wheelListener = (e: WheelEvent) => {
			const scrollEl = document.scrollingElement || document.documentElement
			if (scrollEl.scrollHeight > scrollEl.clientHeight) return

			let deltaY = e.deltaY
			if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
				deltaY *= WHEEL_DELTA_LINE_MULTIPLIER
			} else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
				deltaY *= WHEEL_DELTA_PAGE_MULTIPLIER
			}

			if ((deltaY > 0 && this._wheelDelta < 0) || (deltaY < 0 && this._wheelDelta > 0)) {
				this._wheelDelta = 0
			}
			this._wheelDelta += deltaY

			if (this._wheelDelta > PIP_SCROLL_DIRECTION_THRESHOLD && !this._pipHiddenByScroll) {
				this._pipHiddenByScroll = true
				this._wheelDelta = 0
				if (this._agent?.getAttribute('display-mode') === 'pip') {
					this._applyPipVisualState()
					this._applyAgentLayout('pip')
				}
			} else if (this._wheelDelta < -PIP_SCROLL_DIRECTION_THRESHOLD && this._pipHiddenByScroll) {
				this._pipHiddenByScroll = false
				this._wheelDelta = 0
				if (this._agent?.getAttribute('display-mode') === 'pip') {
					this._applyPipVisualState()
					this._applyAgentLayout('pip')
				}
			}
		}

		document.addEventListener('wheel', this._wheelListener, {
			capture: true,
			passive: true,
		} as EventListenerOptions)
	}

	private _detachWheelFallbackListener(): void {
		if (this._wheelListener) {
			document.removeEventListener('wheel', this._wheelListener, true)
			this._wheelListener = null
		}
		this._wheelDelta = 0
	}

	private _handlePillClick = (): void => {
		if (this._isHoverDevice()) {
			this.setOpen(true)
		} else {
			if (this._pipExpanded) {
				this.setOpen(true)
			} else {
				this._setPipExpanded(true)
			}
		}
	}

	private _handlePillKeydown = (event: Event): void => {
		const key = (event as KeyboardEvent).key
		if (key === 'Enter' || key === ' ') {
			event.preventDefault()
			this.setOpen(true)
		}
	}

	private _hidePipPill(): void {
		if (this._pipPillElement) {
			setStyleValue(this._pipPillElement, 'opacity', '0')
			setStyleValue(this._pipPillElement, 'pointer-events', 'none')
		}
	}

	private _resetHostLayoutStyles(): void {
		for (const name of [
			'position',
			'flex',
			'width',
			'min-width',
			'height',
			'z-index',
			'border-left',
			'transition',
			'opacity',
			'will-change',
		]) {
			resetStyleValue(this, name)
		}
	}

	private _resetAgentLayoutStyles(): void {
		if (!this._agent) return
		for (const name of [
			'position',
			'top',
			'right',
			'bottom',
			'left',
			'transform',
			'z-index',
			'width',
			'height',
			'max-width',
			'max-height',
			'border-radius',
			'overflow',
			'transition',
			'opacity',
			'pointer-events',
		]) {
			resetStyleValue(this._agent, name)
		}
	}

	private _applyUnavailableLayout(): void {
		this._cancelRevealFade()
		this._detachPipInteractionListeners()
		this._detachScrollHideListener()
		this._detachSwipeGestureListeners()
		this._detachWheelFallbackListener()
		this._clearPipCollapseTimer()
		this._pipExpanded = false
		this._pipAnimating = false
		this._hidePipPill()
		this._hideEdgeTab()
		if (this._agent) {
			this._agent.removeAttribute('display-mode')
		}
		this._resetHostLayoutStyles()
		this._resetAgentLayoutStyles()
		this.hidden = true
	}

	private _applyPreReadyLayout(): void {
		this._applyUnavailableLayout()
	}

	private _startHealthWait(): void {
		if (typeof window === 'undefined') {
			return
		}
		this._stopHealthWait()
		this._applyPreReadyLayout()
		this._healthState = 'waiting'
		this._healthTimeoutId = window.setTimeout(() => {
			if (this._healthState !== 'waiting') {
				return
			}
			this._setUnavailable(
				'Initialization timed out before the embedded agent became ready.',
			)
		}, SHELL_HEALTH_TIMEOUT_MS)
	}

	private _stopHealthWait(): void {
		if (this._healthTimeoutId === null || typeof window === 'undefined') {
			return
		}
		window.clearTimeout(this._healthTimeoutId)
		this._healthTimeoutId = null
	}

	private _markHealthy(): void {
		this._stopHealthWait()
		this._healthState = 'healthy'
		this.hidden = false
		this._applyDisplayModeAndLayout()
		this._startRevealFade()
	}

	private _cancelRevealFade(): void {
		if (this._revealFrameId !== null && typeof window !== 'undefined') {
			window.cancelAnimationFrame(this._revealFrameId)
		}
		this._revealFrameId = null
		resetStyleValue(this, 'opacity')
		resetStyleValue(this, 'will-change')
	}

	private _startRevealFade(): void {
		if (typeof window === 'undefined') {
			return
		}
		this._cancelRevealFade()
		setStyleValue(this, 'opacity', '0')
		setStyleValue(this, 'will-change', 'opacity')
		this._revealFrameId = window.requestAnimationFrame(() => {
			this._revealFrameId = null
			setStyleValue(this, 'opacity', '1')
			window.setTimeout(() => {
				resetStyleValue(this, 'will-change')
			}, SHELL_REVEAL_FADE_MS)
		})
	}

	private _setUnavailable(message: string): void {
		this._stopHealthWait()
		this._healthState = 'unavailable'
		this._pendingConnectOptions = null
		this._pipMeasuredHeight = null
		this._setOpenInternal(false, { emit: true, reflect: true })
		this._applyUnavailableLayout()
		this.dispatchEvent(
			new CustomEvent('char-error', {
				bubbles: true,
				composed: true,
				detail: {
					code: 'SHELL_UNAVAILABLE',
					message,
				} satisfies CharErrorDetail,
			}),
		)
	}

	private _recoverFromUnavailable(): void {
		if (this._healthState !== 'unavailable') {
			return
		}
		this._healthState = 'idle'
		this._pipMeasuredHeight = null
		this._detachForwardedListeners()
		if (this._agent) {
			this._agent.remove()
			this._agent = null
		}
		this._forwardedListenersAttached = false
		this._ensureAgent()
		this._syncForwardedAttributes()
		this._applyPreReadyLayout()
		const agent = this._agent as CharAgentElement | null
		if (Object.keys(this._pendingHostContext).length > 0) {
			if (agent) {
				agent.setHostContext(this._pendingHostContext)
			}
			this._pendingHostContext = {}
		}
	}

	private _setOpenInternal(
		nextOpen: boolean,
		options: { emit: boolean; reflect: boolean },
	): void {
		if (this._healthState === 'unavailable' && nextOpen) {
			this._reflectOpenAttribute(false)
			return
		}

		if (nextOpen === this._open) {
			if (options.reflect) {
				this._reflectOpenAttribute(nextOpen)
			}
			return
		}

		this._open = nextOpen
		if (!nextOpen) {
			this._currentPanelWidth = PANEL_WIDTH
		}
		if (options.reflect) {
			this._reflectOpenAttribute(nextOpen)
		}

		this._applyDisplayModeAndLayout()

		if (options.emit) {
			this.dispatchEvent(
				new CustomEvent<CharShellOpenChangeDetail>(SHELL_OPEN_CHANGE_EVENT, {
					bubbles: true,
					composed: true,
					detail: { open: nextOpen },
				}),
			)
		}
	}

	private _reflectOpenAttribute(open: boolean): void {
		this._isApplyingOpenAttribute = true
		applyOpenAttribute(this, open)
		this._isApplyingOpenAttribute = false
	}

	private _flushPendingConnectAndContext(): void {
		if (!this._agent) return

		if (this._pendingConnectOptions) {
			const ok = this._agent.connect(this._pendingConnectOptions)
			if (ok) {
				this._startHealthWait()
			}
		}

		if (Object.keys(this._pendingHostContext).length > 0) {
			this._agent.setHostContext(this._pendingHostContext)
			this._pendingHostContext = {}
		}
	}

	private _handleRequestDisplayMode = (event: Event): void => {
		if (this._healthState === 'unavailable') {
			return
		}
		const detail = (event as CustomEvent<CharRequestDisplayModeDetail>).detail
		if (!isDisplayMode(detail?.mode)) {
			return
		}

		if (detail.mode === 'pip') {
			this.setOpen(false)
			return
		}
		this.setOpen(true)
	}

	private _handleClose = (): void => {
		this.setOpen(false)
	}

	private _handleInitialized = (): void => {
		this._markHealthy()
	}

	private _handleInnerError = (event: Event): void => {
		if (this._healthState !== 'waiting') {
			return
		}
		const detail = (event as CustomEvent<{ message?: string }>).detail
		this._setUnavailable(
			detail?.message ??
				'Embedded agent initialization failed before it became available.',
		)
	}

	private _handleInnerSizeChanged = (event: Event): void => {
		if (!this._agent || this._agent.getAttribute('display-mode') !== 'pip') {
			return
		}
		const detail = (event as CustomEvent<CharSizeChangedDetail>).detail
		if (detail?.displayMode !== 'pip') {
			return
		}

		const reportedHeight = detail?.height
		if (typeof reportedHeight !== 'number' || !Number.isFinite(reportedHeight) || reportedHeight <= 0) {
			return
		}

		const nextMeasuredHeight = Math.max(
			PIP_MIN_COLLAPSED_HEIGHT,
			Math.min(Math.round(reportedHeight), PIP_MAX_MEASURED_HEIGHT),
		)

		if (this._pipAnimating) {
			this._pendingPipMeasuredHeight = nextMeasuredHeight
			return
		}

		if (this._pipMeasuredHeight === nextMeasuredHeight) {
			return
		}

		this._pipMeasuredHeight = nextMeasuredHeight
		this._applyAgentLayout('pip')
	}
}

/**
 * Registers the `<char-agent-shell>` custom element.
 *
 * @param tagName - Optional custom tag name override.
 *
 * @public
 */
export function registerCharShell(tagName = 'char-agent-shell'): void {
	if (typeof window === 'undefined') return
	if (!customElements.get(tagName)) {
		customElements.define(tagName, CharAgentShellElement)
	}
}

registerCharShell()

declare global {
	interface HTMLElementTagNameMap {
		'char-agent-shell': CharAgentShellElement
	}

	/* biome-ignore lint/style/noNamespace: JSX intrinsic-elements augmentation uses namespace syntax by TypeScript design. */
	namespace JSX {
		interface IntrinsicElements {
			'char-agent-shell': {
				open?: boolean
				'api-base'?: string
				apiBase?: string
				'enable-debug-tools'?: boolean
				ref?: unknown
				[key: string]: unknown
			}
		}
	}
}

declare module 'react/jsx-runtime' {
	/* biome-ignore lint/style/noNamespace: JSX intrinsic-elements augmentation uses namespace syntax by TypeScript design. */
	namespace JSX {
		interface IntrinsicElements {
			'char-agent-shell': {
				open?: boolean
				'api-base'?: string
				apiBase?: string
				'enable-debug-tools'?: boolean
				ref?: unknown
				[key: string]: unknown
			}
		}
	}
}
